import { useQuery } from 'convex/react';
import { api } from '../../api';
import { useUserDevice } from './useUserDevice';

interface SiteCategory {
  id: string;
  name: string;
  description: string;
  isAllowed: boolean;
}

interface BrowserConfig {
  humanBlur: boolean;
  nsfwLevel: 'light' | 'medium' | 'strict';
}

export const useSiteCategories = () => {
  const { device, isLoading: isDeviceLoading, deviceConfig } = useUserDevice();

  // Fetch all site category names from Convex
  const allCategoryNames = useQuery(api.siteCategories.list.categories);

  // Fetch device policies (includes blueJayRules with categories, allowlist, blocklist, config)
  const policies = useQuery(
    api.devices.policies.get,
    deviceConfig ? { deviceConfig: deviceConfig as any } : "skip"
  );

  // Extract blueJayRules from policies
  const blueJayRules = policies?.blueJayRules;
  
  // Categories in blueJayRules.categories array are BLOCKED
  // All other categories are ALLOWED
  const blockedCategoryNames: string[] = 
    blueJayRules && typeof blueJayRules === 'object' && Array.isArray(blueJayRules.categories)
      ? blueJayRules.categories
      : [];

  // Map all categories with their blocked/allowed status
  const categories: SiteCategory[] = (allCategoryNames || []).map(name => {
    const isBlocked = blockedCategoryNames.includes(name);
    
    return {
      id: name.toLowerCase().replace(/\s+/g, '_'),
      name,
      description: `Control access to ${name.toLowerCase()} websites`,
      isAllowed: !isBlocked,
    };
  });

  // Helper to get site names from either array or object format
  const getSiteNames = (data: any): string[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') return Object.keys(data);
    return [];
  };

  // Parse allowlist from blueJayRules
  const allowlist = blueJayRules && typeof blueJayRules === 'object'
    ? getSiteNames(blueJayRules.allowlist).map(site => ({ 
        name: site, 
        category: 'Custom'
      }))
    : [];

  // Parse blocklist from blueJayRules
  const blocklist = blueJayRules && typeof blueJayRules === 'object'
    ? getSiteNames(blueJayRules.blocklist).map(site => ({
        name: site,
        category: 'Custom'
      }))
    : [];

  // Parse config from blueJayRules for content filtering
  const config: BrowserConfig = {
    humanBlur: blueJayRules && typeof blueJayRules === 'object' && blueJayRules.config?.human === true,
    nsfwLevel: blueJayRules && typeof blueJayRules === 'object' && blueJayRules.config?.nsfwLevel 
      ? blueJayRules.config.nsfwLevel 
      : 'medium',
  };

  const isLoading = isDeviceLoading || 
    (allCategoryNames === undefined) ||
    (deviceConfig !== null && policies === undefined);

  return {
    categories,
    allowlist,
    blocklist,
    config,
    policies,
    blueJayRules,
    isLoading,
    hasDevice: !!device,
    refetch: () => {
      // Convex handles automatic refetching
    },
  };
};
